<template>
  <div>
    <base-header type="gradient-info" class="header pb-8 pt-5 pt-lg-8 d-flex align-items-center">
        <!-- Header container -->
      <div class="container-fluid d-flex align-items-center">
        <div class="row">
          <div class="col-lg-12">
            <h1 class="display-2 text-white">{{league.Name}}</h1>
            <p class="text-white mt-0 mb-5 title-case">
              {{league.type.replace('_', ' ')}} | {{league.public ? 'Public' : 'Hidden'}}
            </p>
            <a
              href="#!"
              class="btn btn-warning"
              @click.prevent="$router.push({name: 'add league scores', params: {id: id}})"
            >
              Add Scores
            </a>
            <a
              href="#!"
              class="btn btn-default"
              @click.prevent="$router.push({name: 'edit league', params: {id: id}})"
            >
              Edit League
            </a>
            <a
              href="#!"
              class="btn btn-default"
              @click.prevent="modals.uploadSchedule = true"
              v-if="league.type === 'team_live' || league.type === 'mixed'"
            >
              Upload Match Schedule
            </a>
            <a
              href="#!"
              class="btn btn-default"
              @click.prevent="exportPlayerListFlex"
              v-if="league.type === 'individual_flex'"
            >
              Download Player List (csv)
            </a>
            <a
              href="#!"
              class="btn btn-default"
              @click.prevent="exportPlayerListTeam"
              v-if="league.type === 'team_live' || league.type === 'mixed'"
            >
              Download Player List (csv)
            </a>
          </div>
        </div>
      </div>
    </base-header>

    <div class="container-fluid mt--7">
      <div class="row">
        <div class="col-lg-8 pb-2">
          <LeaguePlayersTable
            title="Players"
            :data="playerRoster"
            @addPlayer="modals.addPlayer = true"
            v-if="!isTeamLeague"
            @onRemove="removePlayer"
            @onEdit="editPlayer"
          />
          <LeagueTeamsTable
            title="Teams"
            :data="teamList"
            @addTeam="modals.addTeam = true"
            v-else
            @onRemove="removeTeam"
            @onEdit="editTeam"
            :type="league.type"
          />
        </div>
        <div class="col-lg-4">
          <card>
            <h3 slot="header" class="mb-0">Weeks ({{league.weeks.length}})</h3>
            <div>
              <WeekOverviewCard
                class="week-overview"
                v-for="week in league.weeks"
                :key="week.WeekNumber"
                :week="week"
                @editSchedule="goToSchedule"
                :team="isTeamLeague"
                :league="league"
              />
            </div>
          </card>
        </div>
      </div>
    </div>

    <AddPlayerModal
      :open="modals.addPlayer"
      @close="onAddNewPlayer"
      :league="league"
    />

    <AddTeamModal
      :open="modals.addTeam"
      @close="onAddNewTeam"
      :league="league"
    />

    <EditPlayerModal
      :open="modals.editPlayer"
      @close="onPlayerEdited"
      :player="playerToEdit"
    />

    <EditTeamModal
      :open="modals.editTeam"
      @close="onTeamEdited"
      :team="teamToEdit"
      :league="league"
    />

    <BulkScheduleUploadModal
      :open="modals.uploadSchedule"
      @close="onUploadSchedule"
      :league="league"
    />

    <modal
      :show.sync="modals.removePlayerTeam"
      gradient="danger"
      modal-classes="modal-danger modal-dialog-centered"
    >
      <h6 slot="header" class="modal-title" id="modal-title-notification">Are you sure?</h6>

      <div class="py-3 text-center">
        <i class="fa fa-exclamation-triangle fa-3x"></i>
        <h4 class="heading mt-4">Warning</h4>
        <p>
          This will permanently remove
          {{playerToDelete !== null ? playerToDelete.Name : ''}}
          {{teamToDelete !== null ? teamToDelete.Name : ''}} and all of its
          scorecards and records.
        </p>
      </div>

      <template slot="footer">
        <base-button
          type="white"
          @click="confirmDelete"
        >
          Remove {{teamToDelete !== null ? 'Team' : 'Player'}}
        </base-button>
        <base-button
          type="link"
          text-color="white"
          class="ml-auto"
          @click="cancelDelete"
        >
          Cancel
        </base-button>
      </template>
    </modal>

  </div>
</template>
<script>
import PapaParse from 'papaparse';
import LeaguePlayersTable from './Tables/LeaguePlayersTable.vue';
import LeagueTeamsTable from './Tables/LeagueTeamsTable.vue';
import WeekOverviewCard from '@/components/WeekOverviewCard.vue';
import AddPlayerModal from './AddPlayerModal.vue';
import AddTeamModal from './AddTeamModal.vue';
import EditPlayerModal from './EditPlayerModal.vue';
import EditTeamModal from './EditTeamModal.vue';
import BulkScheduleUploadModal from './BulkScheduleUploadModal.vue';

import AvatarHelper from '@/helpers/Avatars';
import ErrorHandler from '@/helpers/AuthError';

export default {
  name: 'ViewLeague',
  components: {
    LeaguePlayersTable,
    LeagueTeamsTable,
    WeekOverviewCard,
    AddPlayerModal,
    AddTeamModal,
    EditPlayerModal,
    EditTeamModal,
    BulkScheduleUploadModal,
  },
  props: {
    id: [String, Number],
  },
  data() {
    return {
      modals: {
        addPlayer: false,
        addTeam: false,
        removePlayerTeam: false,
        editPlayer: false,
        editTeam: false,
        uploadSchedule: false,
      },
      league: {
        type: '',
        weeks: [],
        profiles: [],
        name: '',
      },
      profiles: [],
      weeks: [],
      teams: [],
      fullTeamList: [],
      teamPlayerProfiles: [],
      playerToDelete: null,
      teamToDelete: null,
      playerToEdit: null,
      teamToEdit: null,
      playerRoster: [],
    };
  },
  mounted() {
    this.getLeague()
      .then(() => {
        if (this.isTeamLeague) {
          return this.getTeams();
        }

        this.getProfiles();
        return this.getPlayerRoster();
      });
    this.getWeeks();
  },
  computed: {
    teamList() {
      const roster = [];
      this.teams.forEach((team) => {
        const playerProfiles = [];
        team.player_profiles.forEach((prof) => {
          playerProfiles.push(this.findTeamProfile(prof.id, this.teamPlayerProfiles));
        });
        roster.push({
          Name: team.Name,
          paid: team.paidInFull,
          rank: 1,
          id: team.id,
          players: playerProfiles,
          division: team.division,
          secondDivision: team.secondDivision,
          flight: team.flight,
          movingFlight: team.movingFlight,
        });
      });
      roster.sort((a, b) => a.Name.toLowerCase().localeCompare(b.Name.toLowerCase()));
      return roster;
    },
    isTeamLeague() {
      if (this.league.type.includes('team') || this.league.type === 'mixed') {
        return true;
      }
      return false;
    },
  },
  methods: {
    exportPlayerListTeam() {
      console.log(this.teamList);
      const playerList = [];
      this.teamList.forEach((team) => {
        team.players.forEach((player) => {
          playerList.push({
            name: player.user.Name,
            email: player.user.email,
            team: team.Name,
          });
        });
      });
      console.log(playerList);
      const csv = PapaParse.unparse(playerList);
      console.log(csv);
      const element = document.createElement('a');
      element.setAttribute('href', `data:text/plain;charset=utf-8,${encodeURIComponent(csv)}`);
      element.setAttribute('download', `${this.league.Name} Roster.csv`);

      element.style.display = 'none';
      document.body.appendChild(element);

      element.click();

      document.body.removeChild(element);
    },
    exportPlayerListFlex() {
      return this.axios
        .get(`/profiles?league_eq=${this.id}`, {
          headers: {
            Authorization: `Bearer ${this.$store.state.authToken}`,
          },
        })
        .then((response) => {
          const dataToExport = response.data.map(player => ({
            name: player.user.Name,
            email: player.user.email,
          }));
          console.log(dataToExport);
          return PapaParse.unparse(dataToExport);
        })
        .then((csv) => {
          console.log(csv);
          const element = document.createElement('a');
          element.setAttribute('href', `data:text/plain;charset=utf-8,${encodeURIComponent(csv)}`);
          element.setAttribute('download', `${this.league.Name} Roster.csv`);

          element.style.display = 'none';
          document.body.appendChild(element);

          element.click();

          document.body.removeChild(element);
        });
    },
    getPlayerRoster() {
      return this.axios
        .get(`/leagues/${this.id}/flexroster`, {
          headers: {
            Authorization: `Bearer ${this.$store.state.authToken}`,
          },
        })
        .then((response) => {
          this.playerRoster = response.data;
          console.log(response.data);
        })
        .catch((error) => {
          ErrorHandler.handleError(error);
        });
    },
    editTeam(team) {
      this.teamToEdit = team;
      this.modals.editTeam = true;
    },
    editPlayer(player) {
      this.playerToEdit = player.profileId;
      this.modals.editPlayer = true;
    },
    deletePlayer() {
      let innerprofile = null;
      return this.getPlayerProfile(this.playerToDelete.profileId)
        .then((profile) => {
          innerprofile = profile;
          const scorecardDeleteJobs = [];
          profile.scorecards.forEach((card) => {
            scorecardDeleteJobs.push(this.deleteScorecard(card.id));
          });
          return Promise.all(scorecardDeleteJobs);
        })
        .then(() => this.axios
          .delete(`/profiles/${innerprofile.id}`, {
            headers: {
              Authorization: `Bearer ${this.$store.state.authToken}`,
            },
          }))
        .then(() => {
          this.modals.removePlayerTeam = false;
          return this.getLeague();
        })
        .then(() => this.getProfiles())
        .then(() => this.getPlayerRoster())
        .catch((error) => {
          ErrorHandler.handleError(error);
        });
    },
    deleteTeam() {
      return this.getTeam(this.teamToDelete.id)
        .then((team) => {
          const deleteJobs = [];
          team.players.forEach((profile) => {
            deleteJobs.push(this.deleteTeamPlayerProfile(profile.profileId));
          });
          return Promise.all(deleteJobs);
        })
        .then(() => this.axios
          .delete(`/teams/${this.teamToDelete.id}`, {
            headers: {
              Authorization: `Bearer ${this.$store.state.authToken}`,
            },
          }))
        .then(() => {
          this.modals.removePlayerTeam = false;
          return this.getLeague();
        })
        .then(() => this.getTeams())
        .catch((error) => {
          ErrorHandler.handleError(error);
        });
    },
    deleteScorecard(cardId) {
      return this.axios
        .delete(`/scorecards/${cardId}`, {
          headers: {
            Authorization: `Bearer ${this.$store.state.authToken}`,
          },
        });
    },
    deleteTeamPlayerProfile(id) {
      return this.axios
        .delete(`/teamprofiles/${id}`, {
          headers: {
            Authorization: `Bearer ${this.$store.state.authToken}`,
          },
        });
    },
    getTeam(teamId) {
      return this.axios
        .get(`/teams/${teamId}`, {
          headers: {
            Authorization: `Bearer ${this.$store.state.authToken}`,
          },
        })
        .then(response => response.data)
        .catch((error) => {
          ErrorHandler.handleError(error);
        });
    },
    getPlayerProfile(profileId) {
      return this.axios
        .get(`/profiles/${profileId}`, {
          headers: {
            Authorization: `Bearer ${this.$store.state.authToken}`,
          },
        })
        .then(response => response.data)
        .catch((error) => {
          ErrorHandler.handleError(error);
        });
    },
    confirmDelete() {
      if (this.playerToDelete !== null) {
        this.deletePlayer();
      }
      if (this.teamToDelete !== null) {
        this.deleteTeam();
      }
    },
    cancelDelete() {
      this.teamToDelete = null;
      this.playerToDelete = null;
      this.modals.removePlayerTeam = false;
    },
    removeTeam(team) {
      this.teamToDelete = team;
      this.modals.removePlayerTeam = true;
    },
    removePlayer(player) {
      this.playerToDelete = player;
      this.modals.removePlayerTeam = true;
    },
    onAddNewPlayer() {
      this.modals.addPlayer = false;
      this.getLeague();
      this.getProfiles();
      this.getPlayerRoster();
    },
    onPlayerEdited() {
      this.modals.editPlayer = false;
      this.getLeague();
      this.getProfiles();
      this.getPlayerRoster();
    },
    onTeamEdited() {
      this.modals.editTeam = false;
      this.getLeague();
      this.getTeams();
    },
    onAddNewTeam() {
      this.modals.addTeam = false;
      this.getLeague();
      this.getTeams();
    },
    onUploadSchedule() {
      this.modals.uploadSchedule = false;
    },
    goToSchedule(week) {
      this.$router.push({ name: 'edit week schedule', params: { id: this.id, week } });
    },
    getWeek(id) {
      let result = null;
      this.weeks.forEach((week) => {
        if (week.id === id) {
          result = week;
        }
      });
      return result;
    },
    getLastScorecard(cards) {
      if (cards.length > 0 && this.weeks.length > 0) {
        // eslint-disable-next-line arrow-body-style
        const sortedCards = cards.sort((a, b) => {
          return this.getWeek(b.week).WeekNumber - this.getWeek(a.week).WeekNumber;
        });
        return sortedCards[0];
      }
      return null;
    },
    getAvatarUrl(user) {
      return AvatarHelper.getAvatar(user);
    },
    getTeams() {
      return this.axios
        .get(`/teams?league_eq=${this.id}`, {
          headers: {
            Authorization: `Bearer ${this.$store.state.authToken}`,
          },
        })
        .then((response) => {
          this.teams = response.data;

          const playerProfileJobs = [];
          response.data.forEach((team) => {
            team.player_profiles.forEach((profile) => {
              playerProfileJobs.push(this.getTeamProfile(profile.id));
            });
          });

          return Promise.all(playerProfileJobs);
        })
        .then((response) => {
          this.teamPlayerProfiles = response;
        })
        .catch((error) => {
          ErrorHandler.handleError(error);
        });
    },
    findTeamProfile(id, profiles) {
      let result = null;
      profiles.forEach((prof) => {
        if (prof.id === id) {
          result = prof;
        }
      });
      return result;
    },
    getLeague() {
      return this.axios
        .get(`/leagues/${this.id}`, {
          headers: {
            Authorization: `Bearer ${this.$store.state.authToken}`,
          },
        })
        .then((response) => {
          this.league = response.data;
          this.league.weeks = this.league.weeks.sort((a, b) => a.WeekNumber - b.WeekNumber);
        })
        .catch((error) => {
          ErrorHandler.handleError(error);
        });
    },
    getProfiles() {
      return this.axios
        .get(`/profiles?league_eq=${this.id}`, {
          headers: {
            Authorization: `Bearer ${this.$store.state.authToken}`,
          },
        })
        .then((response) => {
          this.profiles = response.data;
        })
        .catch((error) => {
          ErrorHandler.handleError(error);
        });
    },
    getTeamProfile(profileId) {
      return this.axios
        .get(`/teamprofiles/${profileId}`, {
          headers: {
            Authorization: `Bearer ${this.$store.state.authToken}`,
          },
        })
        .then(response => response.data)
        .catch((error) => {
          ErrorHandler.handleError(error);
        });
    },
    getWeeks() {
      return this.axios
        .get(`/weeks?league_eq=${this.id}`, {
          headers: {
            Authorization: `Bearer ${this.$store.state.authToken}`,
          },
        })
        .then((response) => {
          this.weeks = response.data;
          this.weeks = this.weeks.sort((a, b) => a.WeekNumber - b.WeekNumber);
        })
        .catch((error) => {
          ErrorHandler.handleError(error);
        });
    },
  },
};
</script>
<style lang="scss" scoped>
  .week-overview{
    margin-bottom: 1rem;

    &:last-child{
      margin-bottom: 0;
    }
  }
  .title-case{
    text-transform: capitalize;
  }
</style>
<style lang="scss">
  .new-player-option{
    display: flex;
    justify-content: center;
    align-items: center;
    padding: .75rem;
    border-top: 1px solid #f5f5f5;
    transition: all .25s;
    font-size: .9rem;
    cursor: pointer;

    &:hover{
      background: #f5f5f5;
    }
  }

  .avatar{
    object-fit: cover;
  }
</style>
